import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { IPromise } from 'angular';
import * as React from 'react';
import { Messages } from '../../codegen/Localize';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { LockStatusHelper, LockUnlockPermissionsState } from '../../ts/helpers/LockStatusHelper';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { ILongRunningAction } from '../../ts/models/ILongRunningAction';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Set } from '../../ts/models/Set';
import { Projects } from '../../ts/resources/Projects';
import { IActionResult } from '../../ts/ServerApi/types/IActionResult';
import { IEmptyResult, IResult } from '../../ts/ServerApi/types/IResponse';
import { LongRunningActionService } from '../../ts/services/LongRunningActionService';
import { ErrorReason } from '../../ts/services/ServerService';
import { IToastOptions, ToasterService, ToastType } from '../../ts/services/ToasterService';
import { PermissionsLockDialog } from './PermissionsLockDialog';

export type PermissionsLockUnlockProps = {
  /** If the component is being used for a project */
  isProject: boolean;

  /** The string to render if the component is not used for a project */
  nonProjectMessage: string;

  /** Projects Resource that is used to get the project's ancestors */
  projectsResource: Projects;   // needed to get the project that is being used's ancestors 

  /** Callback for when a change to lock status has successfully finished */
  updateLockStatus: (lockState: LockUnlockPermissionsState) => void;

  /** The project(s) that this component is for */
  projects?: Set<any>;

  /** ToasterService resource */
  toasterService: ToasterService;

  /** LongRunningActionService resource */
  longRunningActionService: LongRunningActionService
};

export type PermissionsLockUnlockState = {
  showDialog: boolean;
  topLevelProject?: INamedItemInfo; // only needed if there is one project
  busyUnlocking: boolean;
  percentComplete: number;
};

export class PermissionsLockUnlock extends React.Component<PermissionsLockUnlockProps, PermissionsLockUnlockState> {
  public state: PermissionsLockUnlockState = {
    showDialog: false,
    busyUnlocking: false,
    percentComplete: 0,
    topLevelProject: null,
  };

  public componentDidMount(): void {
    this.checkIfTopLevelProject();
  }

  private checkIfTopLevelProject = (): void => {
    // Only consider the top level project when displaying permissions for a single project.
    // When multiple projects are selected, it's possible they could come from different hierarchies and
    // thus have different top level projects. No affordance for this exists in the UI at this time.
    if (this.props.projects && this.props.projects.size() === 1) {
      // server request getProjectAncestors to find the topLevelProject
      this.props.projectsResource.getProjectHierarchy(this.props.projects.first().id).then((result: INamedItemInfo[]) => {
        // set the state to contain the first top level project
        this.setState({ topLevelProject: result && result.length > 0 ? result[0] : this.props.projects.first(), });
      });
    }
  }

  private popToast = (type: ToastType, message: string): void => {
    let toast: IToastOptions;
    switch (type) {
      case ToastType.Error:
        toast = {
          type,
          text: message,
        };
        break;
      case ToastType.Info:
        toast = {
          type,
          text: Messages.lockProjectPermissionsChangeSuccess({
            PROJECT_COUNT: this.props.projects.size(),
            PROJECT_NAME: L10n.elideIfLong(this.props.projects.first().name),
            PERMISSION_SOURCE: message,
            PROJECT_COUNT_LOC: L10n.localizedCount(this.props.projects.size()),
          }),
        };
        break;
    }
    this.props.toasterService.pop(toast);
  }

  private lockProjectPermissions = (): IPromise<IEmptyResult> => {
    return this.props.projectsResource.setProjectControlledPermissions(this.props.projects.pluck('id'))
    .then((result: IEmptyResult) => {
      if (result.errors && result.errors.length) {
        const errorMessage: string = result.errors[0].code === CommonErrorCode.ProjectNotFound ?
          Messages.errorProjectNotFound({ PROJECT_NAME: L10n.elideIfLong(this.props.projects.first().name), }) :
          Messages.errorUnknown();
        this.popToast(ToastType.Error, errorMessage);
      } else {
        this.popToast(ToastType.Info, Messages.lockProjectPermissionsChangeSuccess_locked({ PROJECT_COUNT: this.props.projects.size(), }));
      }
      return result;
    });
  }

  private unlockProjectPermissions = (): IPromise<any> => {
    const permissionsChangeSuccessCallback = (result: ILongRunningAction) => {
      if (result && !result.succeeded) {
        permissionsChangeErrorCallback(ErrorReason.Unknown);
      } else {
        this.popToast(ToastType.Info, Messages.lockProjectPermissionsChangeSuccess_unlocked({ PROJECT_COUNT: this.props.projects.size() }));
        this.setState({ busyUnlocking: false, percentComplete: result.percentComplete, showDialog: false, });
      }
      return result.response;
    };

    const permissionsChangeErrorCallback = (reason: ErrorReason) => {
      this.popToast(ToastType.Error, Messages.errorUnknown());
      this.setState({ busyUnlocking: false, showDialog: false, });
    };

    const progressNotifyCallback = (action: ILongRunningAction) => {
      this.setState({ busyUnlocking: true, percentComplete: action.percentComplete, });
    };

    return this.props.projectsResource.setContentControlledPermissions(this.props.projects.pluck('id'))
    .then((result: IActionResult) => {
      const progress = this.props.longRunningActionService.trackAsyncJobProgress(result.actionId);
      return progress.promise.then(permissionsChangeSuccessCallback, permissionsChangeErrorCallback, progressNotifyCallback);
    });
  }

  private requestUpdateLockStatus = (newLockState: LockUnlockPermissionsState) => {
    if (newLockState === LockUnlockPermissionsState.Locked) {
      return this.lockProjectPermissions();
    } else {
      return this.unlockProjectPermissions();
    }
  }

  private handleButtonClick = (): void => {
    this.setState({ showDialog: true, });
  }

  private handleCloseDialog = (): void => {
    this.setState({ showDialog: false, });
  }

  private handleUpdateLockStatus = (newLockState: LockUnlockPermissionsState): void => {
    this.requestUpdateLockStatus(newLockState).then((result: IResult) => {
      if (!ResultHelper.hasErrors(result)) {
        this.props.updateLockStatus(newLockState);
        this.handleCloseDialog();
      }
    });
  }

  private getLockState = (): LockUnlockPermissionsState => {
    return LockStatusHelper.getLockState(this.props.projects);
  }

  private canLockProjectPermissions = (): boolean => {
    // is a project and there are no children projects
    return this.props.isProject && !this.props.projects.any(p => !p.topLevelProject);
  }

  private getButtonTooltip = (): string => {
    if (this.canLockProjectPermissions()) {
      return '';
    } else if (this.state.topLevelProject) {
      return Messages.lockProjectPermissions_disabledTooltip_withTopLevelProject({ TOP_LEVEL_PROJECT_NAME: this.state.topLevelProject.name, });
    } else {
      return Messages.lockProjectPermissions_disabledTooltip();
    }
  }

  private getMessageIconClass = (lockState: LockUnlockPermissionsState): string => {
    if (this.props.isProject) {
      return LockStatusHelper.getLockStateMessageIconClass(lockState);
    } else {
      return LockStatusHelper.getMessageIconClass(lockState);
    }
  }

  private getLockMessage = (lockState: LockUnlockPermissionsState): string => {
    if (this.props.isProject) {
      return LockStatusHelper.getLockMessage(lockState);
    } else {
      return this.props.nonProjectMessage;
    }
  }

  private renderContent = (lockState: LockUnlockPermissionsState): JSX.Element => {
    return this.state.busyUnlocking ? this.renderBusyUnlocking() : this.renderNotBusy(lockState);
  }

  private renderNotBusy = (lockState: LockUnlockPermissionsState): JSX.Element => {
    return (
      <span className="tb-lock-unlock" data-tb-test-id="permissions-lock">
        <span tb-test-id="permissions-lock-icon" className={ 'tb-lock-icon ' + this.getMessageIconClass(lockState) }></span>
        <span tb-test-id="permissions-label">{ this.getLockMessage(lockState) }</span>
        { this.props.isProject &&
          <span className="tb-permissions-edit-button">
            <ButtonWidget
              buttonType={ ButtonType.Outline }
              handleClick={ this.handleButtonClick }
              disabled={ !this.canLockProjectPermissions() }
              testId="lock-unlock-project-permissions"
              title={ this.getButtonTooltip() }
            >
              <span tb-test-id="permissions-button-text">{ Messages.lockProjectPermissions_edit() }</span>
            </ButtonWidget>
          </span>
        }
      </span>
    );
  }

  private renderBusyUnlocking = (): JSX.Element => {
    return (
      <span className="tb-lock-unlock" tb-test-id="unlock-in-progress">
        { Messages.permissionsUnlockInProgress() }
        <div className="tb-progress-bar">
          <span className="tb-bar">
            <div
              className="tb-progress"
              data-tb-test-id="permissions-progress-bar"
              style={{ width: `${this.state.percentComplete}%` }}
            ></div>
          </span>
          <span className="percent-complete" data-tb-test-id="permissions-progress-number">{ this.state.percentComplete }</span>
        </div>
      </span>
    );
  }

  public render() {
    const lockState = this.getLockState();
    return (
      <span className="tb-permissions-action-right-react" style={{ position: 'static' }}>
        { this.renderContent(lockState) }
        { this.state.showDialog &&
          <PermissionsLockDialog
            projectsNumber={ this.props.projects.size() }
            projectName={ this.props.projects.first().name }
            onCancel={ this.handleCloseDialog }
            onSave={ this.handleUpdateLockStatus }
            currentLockStatus={ lockState }
          />
        }
      </span>
    );
  }
}
